import { Notifications } from '@mui/icons-material';
import { Box, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageDto } from '@/api/generated';
import { NotificationPresentation } from '@/components/notification/NotificationPresentation.comp.tsx';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useGetNotifications } from '@/utils/notifications/useGetNotifications.util.ts';
import { useSetNotificationAsSeen } from '@/utils/notifications/useSetNotificationAsSeen.util.ts';
import { useKnowMeNavigate } from '@/utils/useKnowMeNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export const TopBarNotificationDrawer: React.FC = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslationWithPrefix('topBar.notifications');
  const { t: tNotificationActions } = useTranslationWithPrefix('components.notifications');
  const theme = useTheme();

  const { userId } = useUserContext();
  const { push } = useKnowMeNavigate();
  const { setNotificationAsSeen } = useSetNotificationAsSeen();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const currentLanguage = () => {
    switch (i18n.language) {
      case 'en':
        return LanguageDto.En;
      case 'pl':
        return LanguageDto.Pl;
      default:
        return LanguageDto.En;
    }
  };

  const { isLoading, notifications } = useGetNotifications(
    {
      filter: {
        language: currentLanguage(),
        receiverId: userId,
        seen: false,
      },
      page: 0,
      size: 5,
    },
    60000,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsDrawerMenuClose = () => {
    setAnchorEl(null);
  };

  const goToNotifications = () => {
    handleNotificationsDrawerMenuClose();
    push(RouteEnum.NOTIFICATIONS);
  };

  const unreadNotifications =
    notifications?.items.filter(
      notification => !notification.recipientSeenAt && notification.recipientId === userId,
    ) ?? [];

  const handleMarkLoadedAsRead = async () => {
    if (!unreadNotifications.length) return;

    await Promise.all(
      unreadNotifications.map(notification =>
        setNotificationAsSeen({
          markNotificationAsSeenRequestDto: { userId },
          notificationId: notification.notificationId,
        }),
      ),
    );
  };

  return (
    <>
      <Tooltip title={t('tooltip')}>
        <IconButton
          onClick={handleClick}
          size="large"
          sx={{
            '&:hover': {
              backgroundColor: theme.colors.bgCard3,
              color: theme.colors.textPrimary,
            },
            '&.MuiIconButton-root': {
              border: 'none',
            },
            backgroundColor: open ? theme.colors.bgCard3 : 'transparent',
            borderRadius: '50%',
            color: open ? theme.colors.textPrimary : theme.colors.iconMuted,
            height: '40px',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
            width: '40px',
          }}
        >
          <Badge
            color="error"
            invisible={isLoading || !notifications || notifications.totalResults === 0}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                border: `2px solid ${theme.colors.bgTopbar}`,
              },
            }}
            variant="dot"
          >
            <Notifications sx={{ fontSize: '20px' }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={handleNotificationsDrawerMenuClose}
        open={open}
        slotProps={{
          list: {
            sx: { pb: 0 },
          },
          paper: {
            sx: {
              backgroundColor: theme.colors.bgCard,
              backgroundImage: 'none',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '20px',
              boxShadow: theme.colors.shadowCard,
              mt: 1.5,
              overflow: 'hidden',
              width: 360,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem
          disableRipple
          sx={{
            '&:hover': { backgroundColor: 'transparent' },
            borderBottom: `1px solid ${theme.colors.border}`,
            cursor: 'default',
            display: 'flex',
            justifyContent: 'space-between',
            pb: 1.5,
            px: 2.5,
          }}
        >
          <Typography sx={{ color: theme.colors.textPrimary, fontSize: '14px', fontWeight: 700 }}>
            {t('tooltip')}
          </Typography>
          {unreadNotifications.length > 0 && (
            <Button
              color="primary"
              onClick={handleMarkLoadedAsRead}
              size="large"
              sx={{
                border: 'none',
                color: theme.colors.accent,
                fontSize: '11px',
                fontWeight: 500,
                minWidth: 0,
                px: 1,
                py: 0,
              }}
              variant="text"
            >
              {tNotificationActions('markAsRead')}
            </Button>
          )}
        </MenuItem>
        <Box sx={{ maxHeight: 290, overflowY: 'auto' }}>
          {!isLoading && notifications && notifications.items.length > 0 ? (
            notifications.items.map(notification => (
              <NotificationPresentation
                key={notification.notificationId}
                notification={notification}
                userId={userId}
                variant="topBar"
              />
            ))
          ) : (
            <MenuItem disabled sx={{ color: theme.colors.textSecondary, px: 2.5, py: 2.5 }}>
              {t('noNotifications')}
            </MenuItem>
          )}
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Button
            fullWidth
            onClick={goToNotifications}
            sx={{
              '&:hover': {
                backgroundColor: theme.colors.bgHover,
                borderColor: theme.colors.borderStrong,
              },
              backgroundColor: 'transparent',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '12px',
              color: theme.colors.textPrimary,
              fontSize: '12px',
              fontWeight: 600,
              py: 0.5,
            }}
            variant="outlined"
          >
            {t('seeAll')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};
